import { Select, Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { AVAILABLE_CURRENCIES, CurrencyCodeSchema } from 'types/currency';
import { z } from 'zod/v4';

import {
  CurrencyConversionDateSchema,
  type UpdateCompanyProfileVariables,
  useLoadCompanyProfile,
  useUpdateCompanyProfile,
} from '../api';

const CurrencyFormSchema = z.object({
  currency: CurrencyCodeSchema,
  currencyConversionDate: CurrencyConversionDateSchema,
});

type CurrencyFormValues = z.infer<typeof CurrencyFormSchema>;

const INITIAL_VALUES: CurrencyFormValues = {
  currency: 'EUR',
  currencyConversionDate: 'CURRENT_DATE',
};

export const CurrencyCard = () => {
  const { t } = useTranslation();
  const { companyProfile } = useLoadCompanyProfile();
  const { updateCompanyProfile, isUpdateCompanyProfileLoading } = useUpdateCompanyProfile();

  const form = useForm<CurrencyFormValues>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(CurrencyFormSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded values into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!companyProfile) return;

    const values: CurrencyFormValues = {
      currency: companyProfile.currency,
      currencyConversionDate: companyProfile.currencyConversionDate,
    };
    form.setInitialValues(values);
    form.setValues(values);
  }, [companyProfile]);

  const handleSave = () => {
    const values = form.getValues();
    const payload: UpdateCompanyProfileVariables = {};
    if (form.isDirty('currency')) payload.currency = values.currency;
    if (form.isDirty('currencyConversionDate'))
      payload.currencyConversionDate = values.currencyConversionDate;

    updateCompanyProfile(payload, { onSuccess: () => form.resetDirty() });
  };

  const conversionDateOptions = [
    { value: 'ISSUE_DATE', label: t(i18nKeys.ISSUE_DATE) },
    { value: 'CURRENT_DATE', label: t(i18nKeys.CURRENT_DATE) },
  ];

  return (
    <Panel
      title={t(i18nKeys.FORM.COMPANY.CURRENCY)}
      withBorder
      fullHeight
      pinFooter
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateCompanyProfileLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        <Select
          label={t(i18nKeys.FORM.COMPANY.CURRENCY)}
          allowDeselect={false}
          data={AVAILABLE_CURRENCIES.map(({ code, description }) => ({
            value: code,
            label: description,
          }))}
          {...form.getInputProps('currency')}
        />
        <Select
          label={t(i18nKeys.FORM.COMPANY.CURRENCY_CONVERSION_DATE)}
          allowDeselect={false}
          data={conversionDateOptions}
          {...form.getInputProps('currencyConversionDate')}
        />
      </Stack>
    </Panel>
  );
};
